import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI, publicAPI } from '../services/api';

interface User {
  id: number;
  name: string;
  email: string;
  role: string;
  permissions?: string[];
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<any>;
  register: (data: any) => Promise<void>;
  logout: () => Promise<void>;
  isLoading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionTimeoutMinutes, setSessionTimeoutMinutes] = useState<number | null>(null);

  useEffect(() => {
    const init = async () => {
      // Check for stored token on mount
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      
      if (storedToken && storedUser) {
        const user = JSON.parse(storedUser);
        
        // Check if user has admin role
        if (user.role !== 'admin') {
          // Clear non-admin user data
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          sessionStorage.removeItem('token');
          sessionStorage.removeItem('user');
          console.log('Non-admin user detected, cleared session data');
        } else {
          setToken(storedToken);
          setUser(user);
        }
      }

      // Load session timeout from public maintenance-status (same as main app)
      try {
        const res = await publicAPI.getMaintenanceStatus();
        const minutes =
          res?.data?.data?.security?.session_timeout_minutes ??
          res?.data?.data?.security?.sessionTimeoutMinutes;
        const parsed = parseInt(String(minutes ?? ''), 10);
        if (!isNaN(parsed) && parsed > 0) {
          setSessionTimeoutMinutes(parsed);
        } else {
          setSessionTimeoutMinutes(10);
        }
      } catch (e) {
        setSessionTimeoutMinutes(10);
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await authAPI.login(email, password);
      const { token: newToken, user: newUser } = response.data.data;
      
      // Check if user has admin role
      if (newUser.role !== 'admin') {
        throw new Error('Access denied. Only admin users can access this dashboard.');
      }
      
      setToken(newToken);
      setUser(newUser);
      
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(newUser));
      
      return response; // Return the response for role-based redirection
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message || 'Login failed');
    }
  };

  const register = async (data: any) => {
    try {
      const response = await authAPI.register(data);
      const { token: newToken, user: newUser } = response.data.data;
      
      setToken(newToken);
      setUser(newUser);
      
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(newUser));
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Registration failed');
    }
  };

  const logout = async () => {
    try {
      // Call logout API endpoint if needed
      if (token) {
        try {
          await authAPI.logout();
        } catch (error) {
          console.log('Logout API call failed, but continuing with local logout');
        }
      }
    } catch (error) {
      console.log('Error during logout:', error);
    } finally {
      // Always clear local state regardless of API call result
      setToken(null);
      setUser(null);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
      
      // Preserve admin-specific UI override keys across logout.
      const unauthorisedIpOverrides = localStorage.getItem('admin_unauthorised_ip_overrides_v1');

      // Clear any other cached data
      localStorage.clear();
      sessionStorage.clear();

      if (unauthorisedIpOverrides !== null) {
        localStorage.setItem('admin_unauthorised_ip_overrides_v1', unauthorisedIpOverrides);
      }
    }
  };

  // Idle auto-logout based on sessionTimeoutMinutes
  useEffect(() => {
    if (!token || !sessionTimeoutMinutes || sessionTimeoutMinutes <= 0) {
      return;
    }

    let timeoutId: number | undefined;
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];

    const resetTimer = () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      timeoutId = window.setTimeout(async () => {
        if (localStorage.getItem('token')) {
          await logout();
          try {
            window.location.href = '/login?reason=timeout';
          } catch {
            window.location.href = '/login';
          }
        }
      }, sessionTimeoutMinutes * 60 * 1000);
    };

    events.forEach((ev) => window.addEventListener(ev, resetTimer));
    resetTimer();

    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      events.forEach((ev) => window.removeEventListener(ev, resetTimer));
    };
  }, [token, sessionTimeoutMinutes]);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        register,
        logout,
        isLoading,
        isAuthenticated: !!token,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

